import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

const initialState = {
  agents: [],
  selectedAgent: null,
  stats: null,
  loading: false,
  error: null,
  total: 0,
  pages: 1,
  currentPage: 1,
};

export const fetchAgents = createAsyncThunk(
  'agents/fetchAgents',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/agents', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch agents' });
    }
  }
);

export const fetchAgent = createAsyncThunk(
  'agents/fetchAgent',
  async (agentId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/agents/${agentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch agent' });
    }
  }
);

export const fetchAgentStats = createAsyncThunk(
  'agents/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/agents/stats');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch stats' });
    }
  }
);

export const updateAgent = createAsyncThunk(
  'agents/updateAgent',
  async ({ agentId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/agents/${agentId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to update agent' });
    }
  }
);

export const deleteAgent = createAsyncThunk(
  'agents/deleteAgent',
  async (agentId, { rejectWithValue }) => {
    try {
      await api.delete(`/agents/${agentId}`);
      return agentId;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to delete agent' });
    }
  }
);

const agentsSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    agentUpdated: (state, action) => {
      const index = state.agents.findIndex(a => a.agent_id === action.payload.agent_id);
      if (index !== -1) {
        state.agents[index] = { ...state.agents[index], ...action.payload };
      }
      if (state.selectedAgent?.agent_id === action.payload.agent_id) {
        state.selectedAgent = { ...state.selectedAgent, ...action.payload };
      }
    },
    agentAdded: (state, action) => {
      state.agents.unshift(action.payload);
      state.total += 1;
    },
    agentRemoved: (state, action) => {
      state.agents = state.agents.filter(a => a.agent_id !== action.payload.agent_id);
      state.total -= 1;
    },
    clearSelectedAgent: (state) => {
      state.selectedAgent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.loading = false;
        state.agents = action.payload.agents;
        state.total = action.payload.total;
        state.pages = action.payload.pages;
        state.currentPage = action.payload.current_page;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
      })
      .addCase(fetchAgent.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAgent = action.payload;
      })
      .addCase(fetchAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
      })
      .addCase(fetchAgentStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.agents = state.agents.filter(a => a.agent_id !== action.payload);
        state.total = Math.max(0, state.total - 1);
      });
  },
});

export const { agentUpdated, agentAdded, agentRemoved, clearSelectedAgent } = agentsSlice.actions;
export default agentsSlice.reducer;
